import type { ReactNode, Ref } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ClipboardList, Play, RotateCcw } from 'lucide-react';
import {
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Badge,
  Button,
  SectionLabel,
  TypePill,
  cn,
} from '@wundo/ui';
import { debriefTookPart } from '@wundo/shared/debriefTookPart.ts';
import type { RoleplayCard } from '@wundo/shared/roleplay.ts';
import { formatPracticeHistory, formatRoleplayMeta } from './practiceFormat.ts';
import { fetchRoleplayAttempts } from '@/lib/roleplays.ts';
import { m } from '@/paraglide/messages.js';

export interface PracticeCardProps {
  card: RoleplayCard;
  /** Whether a session is being started — from this card or any other. */
  starting: boolean;
  /**
   * Whether this card is the open one. What is inside the panel stays in the
   * DOM either way, so the card cannot read this off its own markup — and the
   * last attempt is only worth fetching for a card somebody is looking at.
   */
  expanded?: boolean;
  /**
   * The way in to changing this one, for the admins who may. Nothing when the
   * caller cannot author — the row is then the same card everybody else gets.
   */
  edit?: ReactNode;
  /**
   * The grip a card is picked up by, before the title. Nothing on a card
   * nobody arranges — a personal roleplay is one person's own list, and the
   * library is only arranged by its authors.
   */
  handle?: ReactNode;
  /* Where the card is while an admin drags it; see `AccordionItem`. */
  ref?: Ref<HTMLDivElement>;
  dragTransform?: string;
  dragTransition?: string;
  dragging?: boolean;
  /**
   * Whether the reader authors the library. It decides one thing on the card:
   * whether the track or cohort it was written for is named. A member is never
   * handed a card of another one, so the pill would only ever repeat their own.
   */
  canManage?: boolean;
  onStart: () => void;
  onOpenFeedback: () => void;
}

/**
 * What Wundo said the last time this conversation was practised, in two lines.
 *
 * The recap runs to a paragraph — 430 characters at the median in production —
 * so it is clamped and ends in the way to read the rest, which is the feedback
 * itself. Attempts without a debrief are skipped: a session that was never
 * finished has nothing to say about how it went.
 *
 * The query is the feedback dialog's own, key and all, so opening the dialog
 * from here reads what this block already fetched.
 */
function PracticeLastTime({
  card,
  expanded,
  onOpenFeedback,
}: {
  card: RoleplayCard;
  expanded: boolean;
  onOpenFeedback: () => void;
}) {
  const { data: attempts } = useQuery({
    queryKey: ['roleplayAttempts', card.id],
    queryFn: () => fetchRoleplayAttempts(card.id),
    enabled: expanded && card.attempt_count > 0,
  });

  const debrief = attempts?.find((attempt) => attempt.debrief !== null)?.debrief;
  if (!debrief) return null;

  /* Nothing was rated, so there is nothing more to read than this. */
  if (!debriefTookPart(debrief)) {
    return (
      <div>
        <SectionLabel>{m.practice_last_time()}</SectionLabel>
        <p className='mt-3 line-clamp-2 text-md text-ink-body'>
          {`${m.practice_not_taken_part_title()}. ${m.practice_not_taken_part_body()}`}
        </p>
      </div>
    );
  }

  return (
    <div>
      <SectionLabel>{m.practice_last_time()}</SectionLabel>
      <p className='mt-3 line-clamp-2 text-md text-ink-body'>{debrief.recap}</p>
      <Button
        variant='link'
        size='sm'
        className='mt-1.5 text-base'
        onClick={onOpenFeedback}
      >
        {m.practice_read_more()}
      </Button>
    </div>
  );
}

/**
 * One practice conversation on the Oefenen page: what it is, how often you
 * practised it, and the one thing to do with it — opened, the situation and
 * what you are practising.
 *
 * The row's own action is the one that follows from the history: a card you
 * have practised offers the feedback, a card you have not offers the practice.
 * Both are reachable either way — the panel always ends in a start button — but
 * the row says which one the user came for.
 *
 * The type pill is neutral in colour. The label is free text an admin wrote,
 * and mapping words onto the gesprekstype palette would be a guess that reads
 * as a fact.
 */
export function PracticeCard({
  card,
  starting,
  expanded = false,
  edit,
  handle,
  ref,
  dragTransform,
  dragTransition,
  dragging,
  canManage = false,
  onStart,
  onOpenFeedback,
}: PracticeCardProps) {
  const practised = card.attempt_count > 0;

  return (
    /*
     * Opening outlines the card rather than tinting the row: the panel below is
     * part of the same card, and a header that changes colour cuts the card in
     * two exactly when it has become one taller thing.
     */
    <AccordionItem
      ref={ref}
      value={card.id}
      dragTransform={dragTransform}
      dragTransition={dragTransition}
      dragging={dragging}
      className='group transition-shadow hover:shadow-hover data-[open]:border-ink'
    >
      {/*
        The action sits beside the trigger rather than inside it: a button
        within a button is not something a browser or a screen reader can make
        sense of, whatever it looks like.

        Beside on a desktop, under it on a phone. Side by side there is not
        enough width for both: the action keeps its size and the title is the
        thing that gives, which left every card reading "TEST …".
      */}
      <div className='flex items-center gap-4 pr-5.5 max-mobile:flex-col max-mobile:items-stretch max-mobile:gap-0 max-mobile:pr-0 max-mobile:pb-4'>
        {/*
          The grip shares the trigger's line on a phone too: below it, with the
          actions, it would no longer say which card it picks up. It stays
          outside the trigger, so a press on it never opens the card.
        */}
        <div className='flex min-w-0 flex-1 items-center'>
          {/* Its icon lines up with where a title starts on a card without one. */}
          {handle !== undefined && <div className='shrink-0 pl-3'>{handle}</div>}
          <div className='min-w-0 flex-1'>
            {/*
              The chevron is drawn by the row, after the action, so the mark that
              says "this opens" ends the row instead of sitting in the middle of
              it.
            */}
            {/*
              No right padding beside the action: the trigger no longer ends the
              row, so its own edge inset is a second one on top of the gap and the
              card's, and those 22px are what pushed the meta line onto two. On a
              phone the trigger does end the row, and there it keeps them.
            */}
            <AccordionTrigger
              chevron={false}
              className={cn(
                'py-5.5 hover:bg-transparent mobile:pr-0',
                handle !== undefined && 'pl-2',
              )}
            >
              <span className='flex min-w-0 flex-1 flex-col gap-1.25'>
                <span className='flex flex-wrap items-center gap-2'>
                  <span className='min-w-0 break-words mobile:truncate'>
                    {card.title}
                  </span>
                  {card.type_label !== null && (
                    <TypePill type='none'>{card.type_label}</TypePill>
                  )}
                  {/* Who it was written for, for the admin who decided that. */}
                  {canManage && card.track !== null && (
                    <Badge tone='info'>
                      {m.practice_card_audience_track({ title: card.track.title })}
                    </Badge>
                  )}
                  {canManage && card.cohort !== null && (
                    <Badge tone='info'>
                      {m.practice_card_audience_cohort({ title: card.cohort.title })}
                    </Badge>
                  )}
                  {/* Only an admin is ever handed a draft, so the badge needs no further guard. */}
                  {card.status === 'draft' && <Badge>{m.practice_draft()}</Badge>}
                </span>
                {/*
                  One line on a desktop, two on a phone — and the separator goes
                  with the second line rather than staying behind on the first,
                  where it would trail the row as a dot belonging to nothing.
                */}
                <span className='flex items-center gap-2.25 text-base font-normal text-ink-secondary max-mobile:flex-col max-mobile:items-start max-mobile:gap-0.5'>
                  <span>{formatRoleplayMeta(card)}</span>
                  <span
                    aria-hidden='true'
                    className='size-0.75 shrink-0 rounded-full bg-ink-faint max-mobile:hidden'
                  />
                  <span>{formatPracticeHistory(card)}</span>
                </span>
              </span>
            </AccordionTrigger>
          </div>
        </div>

        {/*
          Beside the row's own action, not inside the panel: an admin scanning
          the list for the draft they were writing should not have to open it.
        */}
        <div className='flex shrink-0 items-center gap-2 max-mobile:px-5.5'>
          {edit}

          {practised ? (
            <Button
              variant='quiet'
              shape='block'
              className='px-4 font-medium'
              onClick={onOpenFeedback}
            >
              <ClipboardList className='size-3.75' aria-hidden='true' />
              {m.practice_view_feedback()}
            </Button>
          ) : (
            <Button
              variant='quiet'
              shape='block'
              className='px-4 font-medium'
              disabled={starting}
              onClick={onStart}
            >
              <Play className='size-3.5' aria-hidden='true' />
              {m.practice_start()}
            </Button>
          )}

          {/* At the card's edge on a phone, where the stacked action leaves it stranded mid-row. */}
          <ChevronRight
            aria-hidden='true'
            className='size-4.5 shrink-0 text-ink-secondary transition-transform duration-200 group-data-[open]:rotate-90 max-mobile:ml-auto'
          />
        </div>
      </div>

      <AccordionPanel>
        <div className='flex flex-col gap-5 px-5.5 pb-5.5'>
          <div>
            <SectionLabel>{m.practice_situation()}</SectionLabel>
            <p className='mt-3 text-md text-ink-body'>{card.situation}</p>
          </div>

          <div>
            <SectionLabel>{m.practice_objectives()}</SectionLabel>
            <ul className='mt-3 flex flex-col gap-2.25'>
              {card.learning_objectives.map((objective) => (
                <li key={objective} className='flex items-start gap-2.25'>
                  <span
                    aria-hidden='true'
                    className='mt-2 size-1.5 shrink-0 rounded-full bg-accent'
                  />
                  <span className='text-md text-ink-body'>{objective}</span>
                </li>
              ))}
            </ul>
          </div>

          <PracticeLastTime
            card={card}
            expanded={expanded}
            onOpenFeedback={onOpenFeedback}
          />

          {/*
            No duration line, which the design shows: the API does not know one,
            and a made-up number on a practice card is worse than one line less.
          */}
          <div className='mt-1 flex justify-end'>
            <Button disabled={starting} onClick={onStart}>
              {practised ? (
                <RotateCcw className='size-3.5' aria-hidden='true' />
              ) : (
                <Play className='size-3.5' aria-hidden='true' />
              )}
              {practised ? m.practice_start_again() : m.practice_start()}
            </Button>
          </div>
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
}
